(() => {
  if (window.__AIMA_FLEISCH_V47__) return;
  window.__AIMA_FLEISCH_V47__ = true;

  const DEBUG = false;
  const log = (...a) => DEBUG && console.log("[Fleisch]", ...a);
  const warn = (...a) => DEBUG && console.warn("[Fleisch]", ...a);
  const err = (...a) => DEBUG && console.error("[Fleisch]", ...a);

  const _fetch = (window.__aimaNativeFetch = window.__aimaNativeFetch || (() => {
    const candidates = [window.__originalFetch, window.__nativeFetch, window.fetch];
    for (const fn of candidates) {
      if (typeof fn === "function" && fn.toString().includes("[native code]")) {
        return fn.bind(window);
      }
    }
    return window.fetch.bind(window);
  })());

  const MAX_PIECES = 150;
  const TEMPLATE_ID = "aima-fleisch-widget-template";
  const FETCH_TIMEOUT_MS = 10000;
  const SYNC_DEBOUNCE_MS = 500;   // wait for rapid clicks to settle
  const CART_CACHE_MS = 30000;    // 30s shared cache — matches counter.js

  const WIDGET_SELECTOR = ".aima-fleisch-widget";
  const EX_ITEM_SELECTOR =
    "product-grid-item[data-product-grid-item], is-land.grid-item.grid-product, .grid-item.grid-product";
  const USF_ITEM_SELECTOR = ".usf-sr-product";
  const EX_IMAGE_WRAP_SELECTOR = ".grid-product__image-wrap";
  const USF_IMAGE_WRAP_SELECTOR =
    ".usf-sr-product__image-container, .usf-sr-product-image-container, .usf-sr-product__img";

  const clamp = (n, min, max) => {
    n = Number(n);
    if (!Number.isFinite(n)) n = 0;
    return Math.min(Math.max(n, min), max);
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const isMobile = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(max-width: 480px)").matches;

  // ─── Fetch with timeout ───────────────────────────────────────────────────
  const fetchWithTimeout = (url, options = {}, ms = FETCH_TIMEOUT_MS) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    return _fetch(url, { ...options, signal: ctrl.signal }).finally(() =>
      clearTimeout(timer)
    );
  };

  // ─── 429 circuit breaker (shared with counter.js via window) ─────────────
  // If counter.js already set a cooldown, we respect it and vice versa.
  const getRateLimitedUntil = () => window.__aimaRateLimitedUntil || 0;
  const setRateLimitedUntil = (v) => { window.__aimaRateLimitedUntil = v; };
  const isRateLimited = () => Date.now() < getRateLimitedUntil();

  const enterCooldown = (ms = 2500) => {
    setRateLimitedUntil(Math.max(getRateLimitedUntil(), Date.now() + ms));
  };

  const parseRetryAfterMs = (res) => {
    const h = res?.headers?.get?.("Retry-After");
    if (!h) return 0;
    const n = Number(h);
    return Number.isFinite(n) && n > 0 ? n * 1000 : 0;
  };

  const fetchWithRetry = async (url, options = {}, maxRetries = 2) => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      if (isRateLimited()) {
        const wait = Math.max(0, getRateLimitedUntil() - Date.now());
        err(`Rate limit cooldown, waiting ${wait}ms before ${url}`);
        await sleep(wait);
      }

      const res = await fetchWithTimeout(url, options);
      if (res.status !== 429) return res;

      const backoff = parseRetryAfterMs(res) || (1500 + attempt * 1000);
      err(`429 on ${url}, backing off ${backoff}ms (attempt ${attempt + 1})`);
      enterCooldown(backoff);

      if (attempt === maxRetries) return res;
      await sleep(backoff);
    }
  };

  // ─── Cart cache ───────────────────────────────────────────────────────────
  // Shares the same cache window as counter.js to minimise GET /cart.js calls.
  let cartCache = null;
  let cartCacheTime = 0;
  let cartPromise = null;

  const invalidateCart = () => {
    cartCache = null;
    cartCacheTime = 0;
    cartPromise = null;
  };

  const getCart = (forceFresh = false) => {
    const now = Date.now();
    if (!forceFresh && cartCache && now - cartCacheTime < CART_CACHE_MS) {
      return Promise.resolve(cartCache);
    }
    if (cartPromise) return cartPromise;

    cartPromise = fetchWithRetry("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    })
      .then((r) => {
        if (!r.ok) throw new Error(`cart.js ${r.status}`);
        return r.json();
      })
      .then((c) => {
        cartCache = c;
        cartCacheTime = Date.now();
        cartPromise = null;
        return c;
      })
      .catch((e) => {
        err("cart error", e);
        cartPromise = null;
        return cartCache || { items: [], item_count: 0 };
      });

    return cartPromise;
  };

  const findLine = (cart, id) =>
    cart?.items?.find((it) => Number(it.variant_id) === Number(id));

  // ─── Header cart count ────────────────────────────────────────────────────
  const updateHeaderCartCount = (cart) => {
    const n = Number(cart?.item_count);
    if (!Number.isFinite(n)) return;

    [
      ...document.querySelectorAll(".cart-link__bubble"),
      ...document.querySelectorAll(".site-nav__cart-count"),
      ...document.querySelectorAll("[data-cart-count]"),
      ...document.querySelectorAll("[data-cart-count-bubble]")
    ].forEach((root) => {
      const node =
        root.matches(".cart-link__bubble-num,.cart-count,.count,[data-cart-count]")
          ? root
          : root.querySelector(".cart-link__bubble-num,.cart-count,.count,[data-cart-count]");

      if (node) {
        node.textContent = String(n);
        node.setAttribute("data-count", String(n));
      } else if (!root.childElementCount) {
        root.textContent = String(n);
      }

      root.setAttribute("data-count", String(n));
      if (root.classList.contains("cart-link__bubble")) {
        root.classList.toggle("cart-link__bubble--visible", n > 0);
      }
    });
  };

  // ─── Per-variant debounced sync ───────────────────────────────────────────
  // Replaces the old inflight/pendingTarget loop.
  // Each variant gets its own debounce timer + version stamp.
  // Rapid clicks update desiredPieces instantly (local), then one request fires
  // after SYNC_DEBOUNCE_MS of inactivity — same pattern as counter.js.
  const desiredPieces = new Map();   // variantId → wanted qty
  const syncTimers = new Map();      // variantId → debounce timer
  const syncVersions = new Map();    // variantId → version number

  const getVersion = (id) => syncVersions.get(String(id)) || 0;
  const bumpVersion = (id) => {
    const v = getVersion(id) + 1;
    syncVersions.set(String(id), v);
    return v;
  };

  const doSync = async (variantId, version) => {
    variantId = String(variantId);
    if (getVersion(variantId) !== version) return; // superseded by newer click

    const wanted = clamp(desiredPieces.get(variantId) ?? 0, 0, MAX_PIECES);

    try {
      const res = await fetchWithRetry("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates: { [variantId]: wanted } })
      });

      if (!res.ok) throw new Error(`update.js ${res.status}`);

      // Version check after the request lands — discard if user clicked during flight
      if (getVersion(variantId) !== version) {
        invalidateCart();
        return;
      }

      const newCart = await res.json();
      cartCache = newCart;
      cartCacheTime = Date.now();

      const line = findLine(newCart, variantId);
      const serverQty = line ? clamp(line.quantity, 0, MAX_PIECES) : 0;

      // If server corrected us (e.g. stock limit), update the visual
      const currentDesired = clamp(desiredPieces.get(variantId) ?? 0, 0, MAX_PIECES);
      if (currentDesired === wanted && serverQty !== wanted) {
        desiredPieces.set(variantId, serverQty);
        // Notify widget(s) for this variant to re-render
        document.dispatchEvent(new CustomEvent("aima:fleisch:corrected", {
          detail: { variantId, qty: serverQty }
        }));
      }

      updateHeaderCartCount(newCart);
      document.dispatchEvent(
        new CustomEvent("aima:cart:updated", { detail: { cart: newCart } })
      );
      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { cart: newCart } })
      );
    } catch (e) {
      err("syncQty fail", e);
      invalidateCart();
    }
  };

  const scheduleSync = (variantId) => {
    variantId = String(variantId);
    if (syncTimers.has(variantId)) clearTimeout(syncTimers.get(variantId));

    const version = bumpVersion(variantId);
    syncTimers.set(
      variantId,
      setTimeout(() => {
        syncTimers.delete(variantId);
        doSync(variantId, version);
      }, SYNC_DEBOUNCE_MS)
    );
  };

  // ─── Visual helpers ───────────────────────────────────────────────────────
  const stageOf = (v) => (v <= 0 ? "stage-0" : v === 1 ? "stage-3" : "stage-4");
  const STEP_MS = 60;

  const setStage = (widget, stage) => {
    const ctr = widget.querySelector(".aima-counter");
    if (ctr) ctr.className = `aima-counter ${stage}`.trim();
  };

  const runOpen = (widget) => {
    if (widget.dataset.animating === "1") return;

    if (isMobile()) {
      setStage(widget, "stage-3");
      return;
    }

    widget.dataset.animating = "1";
    setStage(widget, "stage-plus");
    setTimeout(() => setStage(widget, "stage-1"), STEP_MS * 0.6);
    setTimeout(() => setStage(widget, "stage-2"), STEP_MS * 1.2);
    setTimeout(() => {
      setStage(widget, "stage-3");
      widget.dataset.animating = "0";
    }, STEP_MS * 2);
  };
  
  const fmtWeight = (g) =>
  g >= 1000
    ? `${(g / 1000).toFixed(2).replace(/0+$/, "").replace(/\.$/, "").replace(".", ",")} kg`
    : `${g} g`;

  const pieceLabel = () => (isMobile() ? "Stk" : "Stück");

  // ─── Widget init ──────────────────────────────────────────────────────────
  const initWidget = async (widget) => {
    if (!widget || widget.dataset.fv4init === "1") return;
    widget.dataset.fv4init = "1";

    const variantId = String(widget.dataset.variantId || "");
    const gramsPerPiece = parseInt(widget.dataset.stepGrams || "0", 10) || 0;

    const minus = widget.querySelector(".aima-left");
    const plus = widget.querySelector(".aima-plus");
    const countEl = widget.querySelector(".aima-count");
    if (!variantId || !countEl || !plus) return;

    ["click", "mousedown", "pointerdown", "touchstart"].forEach((evt) =>
      countEl.addEventListener(evt, (e) => e.stopPropagation(), true)
    );

    countEl.setAttribute("autocomplete", "off");
    countEl.setAttribute("autocapitalize", "off");
    countEl.setAttribute("spellcheck", "false");

    // Local piece count — always reflects what the user sees
    let pieces = 0;

    const updateText = () => {
      if (gramsPerPiece > 0) {
        countEl.value = pieces > 0 ? fmtWeight(pieces * gramsPerPiece) : "0";
      } else {
        countEl.value = pieces > 0 ? `${pieces} ${pieceLabel()}` : "0";
      }
    };

    const updateVisualState = (newPieces, fromCartInit = false) => {
      const prev = clamp(Number(widget.dataset.prevPieces || "0"), 0, MAX_PIECES);
      newPieces = clamp(newPieces, 0, MAX_PIECES);
      widget.dataset.prevPieces = String(newPieces);
      pieces = newPieces;
      updateText();

      if (!fromCartInit && prev === 0 && newPieces > 0) {
        runOpen(widget);
      } else if (widget.dataset.animating !== "1") {
        setStage(widget, stageOf(newPieces));
      }

      // Keep plus button always enabled at 0 so user can re-add
      if (newPieces <= 0) {
        plus.disabled = false;
        plus.style.opacity = "1";
        plus.style.cursor = "pointer";
        plus.style.pointerEvents = "auto";
      } else {
        const atMax = newPieces >= MAX_PIECES;
        plus.disabled = atMax;
        plus.style.opacity = atMax ? "0.5" : "1";
        plus.style.cursor = atMax ? "default" : "pointer";
        plus.style.pointerEvents = "";
      }
    };

    // Handle server corrections (e.g. stock limit)
    const onCorrected = (e) => {
      if (String(e.detail?.variantId) === variantId) {
        updateVisualState(clamp(e.detail.qty, 0, MAX_PIECES), true);
      }
    };
    document.addEventListener("aima:fleisch:corrected", onCorrected);

    const parseUserInputToPieces = (raw) => {
      if (!raw) return 0;
      let text = String(raw).trim().toLowerCase().replace(",", ".");
      const numMatch = text.match(/([\d.]+)/);
      if (!numMatch) return null;

      const value = parseFloat(numMatch[1]);
      if (!Number.isFinite(value) || value <= 0) return 0;

      if (gramsPerPiece > 0) {
        let totalGrams;
        if (/kg/.test(text)) totalGrams = value * 1000;
        else if (/g/.test(text)) totalGrams = value;
        else return clamp(Math.round(value), 0, MAX_PIECES);

        if (totalGrams <= 0) return 0;
        return clamp(Math.max(1, Math.round(totalGrams / gramsPerPiece)), 0, MAX_PIECES);
      }

      return clamp(Math.round(value), 0, MAX_PIECES);
    };

    // Click handler — purely local, schedules background sync
    const handleChange = (newPieces) => {
      newPieces = clamp(newPieces, 0, MAX_PIECES);
      desiredPieces.set(variantId, newPieces);
      updateVisualState(newPieces);
      scheduleSync(variantId);
    };

    if (minus) {
      minus.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleChange(pieces - 1);
      });
    }

    plus.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleChange(pieces + 1);
    });

    countEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); countEl.blur(); }
      if (e.key === "Escape") { e.preventDefault(); updateText(); countEl.blur(); }
    });

    countEl.addEventListener("blur", () => {
      const parsed = parseUserInputToPieces(countEl.value || "");
      if (parsed === null) { updateText(); return; }
      handleChange(parsed);
    });

    // Init from cart
    try {
      const cart = await getCart();
      const line = findLine(cart, variantId);
      const initialPieces = line ? clamp(line.quantity, 0, MAX_PIECES) : 0;
      desiredPieces.set(variantId, initialPieces);
      updateVisualState(initialPieces, true);
      updateHeaderCartCount(cart);
    } catch {
      desiredPieces.set(variantId, 0);
      updateVisualState(0, true);
    }
  };

  // ─── Template + card scanning (unchanged logic) ───────────────────────────
  const getTemplate = () => document.getElementById(TEMPLATE_ID);

  const cloneWidgetFromTemplate = () => {
    const tpl = getTemplate();
    if (!tpl?.content) return null;
    return tpl.content.firstElementChild?.cloneNode(true) || null;
  };

  const productCache = new Map();

  const inferHandleFromCard = (card) => {
    const link =
      card.querySelector("a[href*='/products/']") ||
      card.querySelector("a.grid-item__link") ||
      card.querySelector("a");
    const href = link?.getAttribute("href");
    if (!href) return null;
    try {
      const url = new URL(href, window.location.origin);
      const parts = url.pathname.split("/").filter(Boolean);
      const idx = parts.lastIndexOf("products");
      return idx !== -1 && parts[idx + 1] ? parts[idx + 1] : parts[parts.length - 1] || null;
    } catch { return null; }
  };

  const fetchProductInfo = async (handle) => {
    handle = String(handle || "").trim();
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);

    try {
      const res = await fetchWithRetry(`/products/${handle}.js`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" }
      });
      if (!res.ok) throw new Error(`product.js ${res.status}`);
      const product = await res.json();
      const tags = Array.isArray(product.tags) ? product.tags : [];
      const info = {
        isMeat: tags.includes("Gr_Button"),
        variantId: product.variants?.[0]?.id ? String(product.variants[0].id) : null
      };
      productCache.set(handle, info);
      return info;
    } catch (e) {
      err("fetch product fail", handle, e);
      return null;
    }
  };

  const parseGramsFromText = (text) => {
    const t = String(text || "").toLowerCase();
    const kg = t.match(/(\d+(?:[.,]\d+)?)\s*kg/);
    if (kg) return Math.round(parseFloat(kg[1].replace(",", ".")) * 1000);
    const g = t.match(/(\d+)\s*g/);
    if (g) return parseInt(g[1], 10);
    return 0;
  };

  const getGramsFromCard = (card) => {
    const direct =
      card.getAttribute("data-step-grams") ||
      card.getAttribute("data-quantity-grams") ||
      card.dataset?.stepGrams ||
      card.dataset?.quantityGrams;
    if (direct && Number(direct) > 0) return String(direct);

    const title =
      card.querySelector(".grid-product__title")?.textContent ||
      card.querySelector(".usf-sr-product__title")?.textContent ||
      card.textContent || "";
    return String(parseGramsFromText(title) || 0);
  };

  const hasNormalAimaCounter = (scopeEl) =>
    !!scopeEl.querySelector?.(".counter-container[data-aima-counter]");

  const isMeatFromDataset = (card) => {
    const v = card.getAttribute("data-meat-product") || card.dataset?.meatProduct;
    return String(v) === "true" || String(v) === "1";
  };

  const shouldAttachToCard = async (card) => {
    if (!card) return false;
    if (isMeatFromDataset(card)) return true;
    const handle = inferHandleFromCard(card);
    if (!handle) return false;
    const info = await fetchProductInfo(handle);
    return !!info?.isMeat;
  };

  const getVariantIdFromCard = async (card) => {
    const direct =
      card.getAttribute("data-variant-id") ||
      card.dataset?.variantId ||
      card.querySelector("[data-variant-id]")?.getAttribute("data-variant-id");
    if (direct) return String(direct);
    const handle = inferHandleFromCard(card);
    if (!handle) return null;
    const info = await fetchProductInfo(handle);
    return info?.variantId || null;
  };

  const attachWidget = async (card, imageWrap) => {
    if (!card || !imageWrap) return;

    const existingAll = imageWrap.querySelectorAll(WIDGET_SELECTOR);
    if (existingAll.length > 1) {
      for (let i = 1; i < existingAll.length; i++) existingAll[i].remove();
    }
    if (existingAll.length >= 1) { initWidget(existingAll[0]); return; }

    if (card.dataset.fleischAttaching === "1") return;
    if (imageWrap.dataset.fleischAttaching === "1") return;
    card.dataset.fleischAttaching = "1";
    imageWrap.dataset.fleischAttaching = "1";

    try {
      if (hasNormalAimaCounter(imageWrap) && !isMeatFromDataset(card)) return;

      const ok = await shouldAttachToCard(card);
      if (!ok) return;

      const variantId = await getVariantIdFromCard(card);
      if (!variantId) return;
      if (imageWrap.querySelector(WIDGET_SELECTOR)) return;

      const widget = cloneWidgetFromTemplate();
      if (!widget) { warn(`Missing template #${TEMPLATE_ID}`); return; }

      widget.dataset.variantId = String(variantId);
      widget.dataset.stepGrams = String(getGramsFromCard(card) || "0");

      imageWrap.appendChild(widget);
      initWidget(widget);
      card.dataset.fleischAttached = "1";
    } finally {
      card.dataset.fleischAttaching = "0";
      imageWrap.dataset.fleischAttaching = "0";
    }
  };

  const scanExpanse = async () => {
    const nodes = Array.from(document.querySelectorAll(EX_ITEM_SELECTOR));
    for (const raw of nodes) {
      const card =
        raw.matches?.("product-grid-item[data-product-grid-item]")
          ? raw
          : raw.querySelector?.("product-grid-item[data-product-grid-item]") || raw;
      const imageWrap =
        card?.querySelector?.(EX_IMAGE_WRAP_SELECTOR) ||
        raw.querySelector?.(EX_IMAGE_WRAP_SELECTOR);
      if (!card || !imageWrap) continue;
      await attachWidget(card, imageWrap);
    }
  };

  const scanUSF = async () => {
    const nodes = Array.from(document.querySelectorAll(USF_ITEM_SELECTOR));
    for (const card of nodes) {
      const imageWrap = card.querySelector(USF_IMAGE_WRAP_SELECTOR);
      if (!imageWrap) continue;
      await attachWidget(card, imageWrap);
    }
  };

  const initAll = async () => {
    document.querySelectorAll(WIDGET_SELECTOR).forEach((w) => initWidget(w));
    await scanExpanse();
    await scanUSF();
  };

  const scheduleScan = (() => {
    let t = null;
    return () => {
      if (t) return;
      t = setTimeout(() => { t = null; initAll(); }, 80);
    };
  })();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.addedNodes?.length) { scheduleScan(); return; }
    }
  });

  if (document.body) mo.observe(document.body, { childList: true, subtree: true });

  if (window.Shopify?.designMode) {
    document.addEventListener("shopify:section:load", scheduleScan);
    document.addEventListener("shopify:section:reorder", scheduleScan);
    document.addEventListener("shopify:section:select", scheduleScan);
  }
})();